/**
 * This middleware fires events to Braze
 */

import {
	AUTH_SESSION_START,
	BOOTSTRAP_GET_USER_SUCCESS,
	LOG_OUT,
	POST_USER_SUCCESS,
} from 'stash/actions';
import { BRAZE_ENV_APP_KEY, BRAZE_SDK_ENDPOINT } from 'stash/constants';
import { tryWarn } from 'stash/utils';
import * as braze from '@braze/web-sdk';

type BrazeAction = {
	type: string;
	response?: {
		user?: {
			braze_id?: string;
		};
	};
};

// No easy way to track this in the Braze SDK so doing it here.
let sessionIsOpen = false;

const initializeBraze = tryWarn(() => {
	if (process.env.NODE_ENV === 'test') return;

	braze.initialize(BRAZE_ENV_APP_KEY, {
		baseUrl: BRAZE_SDK_ENDPOINT,
		enableLogging: /-/.test(window.location.hostname),
		allowUserSuppliedJavascript: true,
	});
});

const destroyBrazeInstance = tryWarn(() => {
	sessionIsOpen = false;
	braze.removeAllSubscriptions();
	braze.destroy();
});

const setBrazeUser = tryWarn(async (brazeId: string) => {
	if (sessionIsOpen) return;
	sessionIsOpen = true;
	try {
		braze.automaticallyShowInAppMessages();
		// In Braze Web SDK v6+, changeUser returns a Promise and must be awaited
		await braze.changeUser(brazeId);
		braze.openSession();
	} catch (error) {
		// Reset flag so future attempts aren't blocked if this fails
		sessionIsOpen = false;
		throw error;
	}
});

export const logBrazeCustomEvent = tryWarn(
	(eventName: string, properties?: Record<string, unknown>) => {
		braze.logCustomEvent(eventName, properties);
	}
);

const brazeReducer =
	() =>
	(next: (action: BrazeAction) => unknown) =>
	(action: BrazeAction): unknown => {
		switch (action.type) {
			case AUTH_SESSION_START:
				initializeBraze();
				break;
			case LOG_OUT:
				destroyBrazeInstance();
				break;
			case POST_USER_SUCCESS:
			case BOOTSTRAP_GET_USER_SUCCESS: {
				const brazeId = action.response?.user?.braze_id;
				if (brazeId) {
					setBrazeUser(brazeId);
				}
				break;
			}
			default:
				break;
		}

		return next(action);
	};

export default brazeReducer;
